import React, { useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";

import AttachFileIcon from "@mui/icons-material/AttachFile";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { Breadcrumb, SimpleCard } from "app/components";
import { Formik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { GETALLUSERS } from "app/redux/actions/users";
import { createLeaveRequest } from "app/redux/actions/leave";
import { isImageFile } from "app/assets/genericActions";
import { LIVE_URL } from "config";
import Loading from "app/components/MatxLoading";
import { useAuth } from 'app/hooks/useAuth';
import { PATH } from "../../../config";
import dayjs from "dayjs";

const CreateLeaveRequest = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useAuth();
  const loggedInUser = useSelector((state) => state.user.user);
  const user = useSelector((state) => state.user);
  const leave = useSelector((state) => state.leave);
  useEffect(() => {
    if (
      loggedInUser?.token &&
      (loggedInUser.userType !== "employee")
    ) {
      dispatch(
        GETALLUSERS(
          {
            page: 1,
            limit: 1000,
            teamLeadId:
              loggedInUser.userType === "teamlead" ? loggedInUser?.id : null,
          },
          loggedInUser
        )
      );
    }
  }, [dispatch, loggedInUser]);
  const handleFileUpload = (event, fieldName, setFieldValue) => {
    const file = event.target.files[0];
    if (!file) return;
    setFieldValue(fieldName, file); // Store File object
    toast.success(`${fieldName.replace("_", " ")} selected successfully!`);
  };
  const handleRemoveFile = (fieldName, setFieldValue) => {
    setFieldValue(fieldName, null);
  };

  const userListForAdmin = user?.allUsers?.users?.filter(
    (user) => user.id !== loggedInUser?.id
  );

  const validationSchema = Yup.object().shape({
    leaveStartDate: Yup.date().required("Start date is required").nullable(),
    leaveEndDate: Yup.date()
      .required("End date is required")
      .nullable()
      .min(Yup.ref("leaveStartDate"), "End date can't be before start date"),

    leaveType: Yup.string().required("Leave type is required"),
    reason: Yup.string().required("A reason for the leave is required"),
    userId:
      loggedInUser.userType === "admin"
        ? Yup.string().nullable().required("You must select an employee")
        : Yup.string().nullable(),
  });

  const handleFormSubmit = (values) => {
    const formData = new FormData();

    for (const key in values) {
      if (values[key] instanceof File) {
        formData.append(key, values[key]);
      } else if (key === "userId") {
        formData.append(key, values.userId || loggedInUser.id);
      } else {
        formData.append(key, values[key] ?? "");
      }
    }

    // formData.append(
    //   "startDate",
    //   values.leaveStartDate.toISOString().split("T")[0]
    // );

    // formData.append("endDate", values.leaveEndDate.toISOString().split("T")[0]);
    formData.append(
      "startDate",
      dayjs(values.leaveStartDate).format("YYYY-MM-DD")
    );
    formData.append(
      "endDate",
      dayjs(values.leaveEndDate).format("YYYY-MM-DD")
    );
    const onSaveSuccess = () => {
      toast.success("Leave request created successfully!");
      if (auth.userType === 'admin') {
        navigate(PATH.ALLREQUESTS);
      }
      else if (auth.userType === 'teamlead' && values.userId === "") {
        navigate(PATH.ALLMYREQUESTS);
      }
      else if (auth.userType === 'teamlead' && values.userId !== "") {
        navigate(PATH.ALLREQUESTS);
      }
      else if (auth.userType === 'employee') {
        navigate(PATH.ALLREQUESTS);
      }

    };
    const onfailure = (message) => {
      toast.error(message);
    };
    dispatch(
      createLeaveRequest(
        formData,
        loggedInUser?.token || null,
        onSaveSuccess,
        onfailure
      )
    );
  };

  return (
    <SimpleCard>
      <Box>
        <Breadcrumb routeSegments={[{ name: "Create Leave Request" }]} />
      </Box>
      <Box padding={2}>
        <Formik
          initialValues={{
            leaveStartDate: new Date(),
              leaveEndDate: new Date(),
            leaveType: "",
            reason: "",
            userId: "",
          }}
          validationSchema={validationSchema}
          onSubmit={handleFormSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
            setFieldTouched,
            isSubmitting,
            validateForm,
          }) => {
            const handleButtonClick = (e) => {
              e.preventDefault();
              validateForm().then((formErrors) => {
                if (formErrors.leaveEndDate) {
                  toast.error(formErrors.leaveEndDate);
                } else if (formErrors.leaveStartDate) {
                  toast.error(formErrors.leaveStartDate);
                }
                handleSubmit(e);
              });
            };

            return (
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  {auth.userType === 'admin' ?
                    <Grid item xs={12} sm={6}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          label="Leave Start Date *"
                          value={values.leaveStartDate || new Date()}
                          onChange={(date) => {
                            setFieldValue("leaveStartDate", date ?? null);
                          }}
                          onClose={() => setFieldTouched("leaveStartDate", true)}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              name: "leaveStartDate",
                              error:
                                touched.leaveStartDate && Boolean(errors.leaveStartDate),
                              helperText: touched.leaveStartDate && errors.leaveStartDate,
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>
                    :
                    <Grid item xs={12} sm={6}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          label="Leave Start Date *"
                          value={values.leaveStartDate || new Date()}
                          onChange={(date) => {
                            setFieldValue("leaveStartDate", date ?? null);
                          }}
                          onClose={() => setFieldTouched("leaveStartDate", true)}
                          // disabled
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              name: "leaveStartDate",
                              error:
                                touched.leaveStartDate && Boolean(errors.leaveStartDate),
                              helperText: touched.leaveStartDate && errors.leaveStartDate,
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>
                  }
                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="Leave End Date *"
                        value={values.leaveEndDate}
                        onChange={(date) => {
                          setFieldValue("leaveEndDate", date ?? null);
                        }}
                        onClose={() => setFieldTouched("leaveEndDate", true)}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            name: "leaveEndDate",
                            error:
                              touched.leaveEndDate &&
                              Boolean(errors.leaveEndDate),
                            helperText:
                              touched.leaveEndDate && errors.leaveEndDate,
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>
                  {(loggedInUser.userType === "admin" ||
                    loggedInUser.userType === "teamlead") && (
                      <Grid item xs={12} sm={6}>
                        <FormControl
                          fullWidth
                          error={touched.userId && Boolean(errors.userId)}
                        >
                          <InputLabel>
                            Select User{" "}
                            {loggedInUser.userType === "admin"
                              ? "*"
                              : "(Optional, for team member)"}
                          </InputLabel>
                          <Select
                            name="userId"
                            value={values.userId}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            label={`Select User ${loggedInUser.userType === "admin"
                              ? "*"
                              : "(Optional, for team member)"
                              }`}
                          >
                            {(
                              (loggedInUser.userType === "admin"
                                ? userListForAdmin
                                : user?.allUsers?.users) || []
                            ).map((user, index) => {
                              return (
                                <MenuItem key={index} value={user.id}>
                                  {user.name}
                                </MenuItem>
                              );
                            })}
                          </Select>
                          {touched.userId && errors.userId && (
                            <FormHelperText>{errors.userId}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    )}

                  <Grid item xs={12} sm={6}>
                    <FormControl
                      fullWidth
                      error={touched.leaveType && Boolean(errors.leaveType)}
                    >
                      <InputLabel>Leave Type *</InputLabel>
                      {/* <Select
                        name="leaveType"
                        value={values.leaveType}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        label="Leave Type *"
                      >
                        <MenuItem value="Sick">Sick</MenuItem>
                        <MenuItem value="Casual">Casual</MenuItem>
                        <MenuItem value="Urgent">Urgent</MenuItem>
                      </Select> */}
                      <Select
                        name="leaveType"
                        value={values.leaveType}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        label="Leave Type *"
                      >
                        <MenuItem value="Sick">Sick</MenuItem>
                        <MenuItem value="Casual">Casual</MenuItem>
                        <MenuItem value="Urgent">Urgent</MenuItem>
                        {auth.userType === 'admin' && (
                          <MenuItem value="Un-Paid">Un-Paid</MenuItem>
                        )}
                      </Select>
                      {touched.leaveType && errors.leaveType && (
                        <FormHelperText>{errors.leaveType}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="reason"
                      label="Reason *"
                      multiline
                      rows={4}
                      value={values.reason}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.reason && Boolean(errors.reason)}
                      helperText={touched.reason && errors.reason}
                    />
                    <Grid item lg={6} md={6} sm={12} xs={12}>
                      <Button
                        style={{ marginTop: "20px" }}
                        variant="outlined"
                        component="label"
                        startIcon={<AttachFileIcon />}
                      >
                        Upload Attachment
                        <input
                          type="file"
                          hidden
                          onChange={(event) =>
                            handleFileUpload(event, "document", setFieldValue)
                          }
                        />
                      </Button>

                      {/* Show selected file with remove */}
                      {values.document && (
                        <Box
                          mt={1}
                          display="flex"
                          alignItems="center"
                          gap={2}
                        >
                          {isImageFile(values.document) ? (
                            <img
                              src={
                                typeof values.document === "string"
                                  ? LIVE_URL + values.document
                                  : URL.createObjectURL(values.document)
                              }
                              alt="CNIC Front"
                              style={{
                                width: 100,
                                height: "auto",
                                borderRadius: 4,
                              }}
                            />
                          ) : (
                            <AttachFileIcon fontSize="large" />
                          )}
                          <Button
                            size="small"
                            color="error"
                            onClick={() =>
                              handleRemoveFile("document", setFieldValue)
                            }
                          >
                            Remove
                          </Button>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                  {leave.loading === true ? (
                    <Loading />
                  ) : (
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={leave.loading}
                        onClick={handleButtonClick}
                      >
                        {leave.loading ? "Submitting..." : "Create Request"}
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </form>
            );
          }}
        </Formik>
      </Box>
    </SimpleCard>

  );
};

export default CreateLeaveRequest;
